import React, { useState, useEffect } from 'react'
import './landing.css';
import { motion } from 'framer-motion';
import C2wCountCard from '../../../common/C2wHome/landing/countCard/countCard';
import C2wEventCard from '../../../common/C2wHome/landing/eventCard/eventCard';

function C2wLanding({ onCompaniesClick, onPlacementsClick, onIncubatorsClick }) {

  const [countData, setCountData] = useState(null);


  /**
 * @hook useEffect - Load JSON Data on Mount
 * @author Vishal Lohbande
 *
 * Description:
 * - Fetches data from a local JSON file (`/website.json`) when the component mounts.
 * - Parses the response and updates local state using `setCountData`.
 *
 * Behavior:
 * - Defines an async function `fetchData` inside the effect scope.
 * - Calls `fetchData()` immediately after component is mounted.
 * - If the fetch request fails (`res.ok` is false), an error is thrown.
 * - On success, response is parsed into JSON and passed to `setCountData` to update state.
 *
 * Notes:
 * - Runs only once due to empty dependency array `[]`.
 * - Suitable for static local JSON files bundled in the public folder.
 * - If deployed, `/website.json` must exist in the `public` directory for client-side access.
 */
  useEffect(() => {
    const fetchData = async () => {
      const res = await fetch('/website.json');
      if (!res.ok) throw new Error('Failed to load JSON');
      const json = await res.json();
      setCountData(json);
    };
    fetchData();
  }, []);

  /**
   * @state windowWidth - Tracks the current width of the browser window.
   * @hook useEffect - Window Resize Listener
   * @author Vishal Lohbande
   * Description:
   * - Initializes `windowWidth` state with the current width of the browser window.
   * - Adds an event listener to update the state whenever the window is resized.
   *
   * Behavior:
   * - `handleResize` updates the `windowWidth` state using `window.innerWidth`.
   * - The resize listener is attached once on component mount.
   * - On unmount, the event listener is properly cleaned up to prevent memory leaks.
   *
   * Use Case:
   * - Responsive UI adjustments (e.g., conditional rendering based on screen width).
   * - Dynamic layout shifts or breakpoints detection in React components.
   *
   * Notes:
   * - This setup ensures that UI can respond in real-time to screen size changes.
   */
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  /**
 * @constant countArrContent
 * @author Vishal Lohbande
 *
 * Description:
 * - An array of objects representing summary sections for the landing page.
 * - Each object includes a heading, a dynamic count from `countData`, and an array of image paths.
 *
 * Fields:
 * - `heading` {string} - The title of the section (e.g., "Students", "Placements", "Companies").
 * - `count` {number | undefined} - A dynamic count value from the `countData` object.
 * - `imgArr` {string[]} - List of image URLs used to visually represent the section.
 *
 * Usage:
 * - Rendered in a loop to create statistic tiles or counters with user/company thumbnails.
 *
 * Dependencies:
 * - Assumes `countData` is fetched and available in the parent state.
 *
 * Example:
 * - Used in dashboard or landing page to show user growth, placement stats, or partners.
 */
  const countArrContent = [
    {
      heading: 'Students',
      count: countData?.studentCount,
      imgArr: [
        '/assets/images/landing/Tejaswini.png',
        '/assets/images/landing/Abhishek.png',
        '/assets/images/landing/ruchira.png',
        '/assets/images/landing/shubham.png',
      ]
    },
    {
      heading: 'Placements',
      count: countData?.placementCount,
      imgArr: [
        '/assets/images/landing/utkarsh.png',
        '/assets/images/landing/Harshal.png',
        '/assets/images/landing/Vaishnavi.png',
        '/assets/images/landing/plus.png',

      ]
    },
    {
      heading: 'Companies',
      count: countData?.companiesCount,
      imgArr: [
        '/assets/images/landing/amazon.jpg',
        '/assets/images/landing/nVedia.png',
        '/assets/images/landing/enable.png',
        '/assets/images/landing/plus.png',
      ]
    }
  ]


  const eventCardArr = [
    {
      imgSrc: '/assets/images/landing/incubators.svg',
      tagTitle: 'Training & Internship Programs',

    },
    {
      imgSrc: '/assets/images/landing/superx.svg',
      tagTitle: 'A project building platform for you',

    },
    {
      imgSrc: '/assets/images/landing/quizeethon.svg',
      tagTitle: 'Quiz competition & quick thinking.',

    },

  ]

  const [activeIndex, setActiveIndex] = useState(0);

  /**
 * @hook useEffect - Auto Cycle Active Index
 * @author Vishal Lohbande
 * Description:
 * - Sets up an interval to automatically update `activeIndex` every 1000 milliseconds (1 second).
 * - Cycles through indices in `eventCardArr` array repeatedly in a loop.
 *
 * Behavior:
 * - Uses a functional state update (`prevIndex => ...`) to ensure state consistency.
 * - Increments the current index by 1.
 * - Wraps around to 0 when reaching the end of `eventCardArr` (`modulus` operation).
 *
 * Cleanup:
 * - Clears the interval on component unmount to prevent memory leaks or multiple intervals running.
 *
 * Use Case:
 * - Typically used for auto-rotating sliders, carousels, or highlight sections on landing pages.
 *
 * Notes:
 * - Interval duration (1000ms) can be adjusted for faster or slower cycling.
 * - The empty dependency array ensures this effect runs once on mount.
 */
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % eventCardArr.length);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  /**
   * @constant renderedLeftDiv
   * @author Vishal Lohbande
   *
   * Description:
   * - Maps over the `countArrContent` array to generate an array of `C2wCountCard` components.
   * - Passes each item (`obj`) and its index as props to the `C2wCountCard` component.
   *
   * Usage:
   * - Used to render multiple statistic cards dynamically on the UI.
   * - Each card displays data like heading, count, and related images.
   *
   * Notes:
   * - Assumes `countArrContent` is defined and populated.
   * - Each child component should have a unique key prop; consider adding `key={index}` for React list rendering.
   */
  const renderedLeftDiv = countArrContent?.map((obj, index) => (
    <C2wCountCard
      onClick={
        index === 1
          ? onPlacementsClick
          : index === 2
            ? onCompaniesClick
            : null
      }
      obj={obj}
      index={index}
      key={index}
    />
  ));

  /**
 * @constant renderedEventsCards
 * @author Vishal Lohbande
 *
 * Description:
 * - Maps over the `eventCardArr` array to create an array of `C2wEventCard` components.
 * - Passes each event object (`obj`), its index, the current `activeIndex`, and
 *   a click handler `onIncubatorsClick` as props to each card.
 *
 * Props:
 * - `obj` {object} - The data for the specific event card.
 * - `index` {number} - The position of the card in the array.
 * - `activeIndex` {number} - Used to determine which card is currently active/visible.
 * - `onIncubatorsClick` {function} - Callback to handle incubators click event.
 *
 * Usage:
 * - Dynamically renders event highlight cards on the UI.
 * - Supports interaction via the click handler and highlights active cards based on `activeIndex`.
 *
 * Notes:
 * - Uses `key={index}` for React’s list rendering optimization.
 * - Make sure `activeIndex` updates correctly to reflect active card status.
 */
  const renderedEventsCards = eventCardArr?.map((obj, index) => (
    <C2wEventCard onIncubatorsClick={onIncubatorsClick} key={index} obj={obj} index={index} activeIndex={activeIndex} />
  ))
  

  return (
    windowWidth > 620 ? (
      <div className='C2WLanding_ctn'>
        <motion.div className='C2w_LogoAnimation'
          layout
          initial={{ scale: 1, opacity: 1 }}
          animate={{ scale: 0, opacity: 0 }}
          transition={{ delay: 0.5, duration: 2, ease: "easeOut" }}
        >
          <img src='/assets/images/landing/Core2web.png' alt='c2w-img' />
        </motion.div >
        <div className='C2WLanding_heading_ctn'>
          <motion.div className='C2WLanding_heading_motion'
            // layout
            initial={{ y: 0, opacity: 0, }}
            animate={{ y: 80, opacity: 1 }}
            transition={{ delay: 2, duration: 1.5, ease: "easeOut" }}>
            <div className='C2WLanding_heading'>
              <div className='C2WLanding_heading_main'>{'IT Training | Programming Expertise'}</div>
              <div className='C2WLanding_heading_Submain'>{'Hands-On Projects | Entrepreneurship'}</div>
            </div>
          </motion.div>
        </div>
        <div className='C2wLanding_Content_ctn'>
          <motion.div
            layout
            className='C2wLanding_Content_ctn_left_motion'
            initial={{ y: 300, x: 600, scale: 0, opacity: 0 }}
            animate={{ y: 0, x: 0, scale: 1, opacity: 1 }}
            transition={{ delay: 3.8, duration: 1.8, ease: "easeInOut" }}
          >
            <div className='C2wLanding_Content_ctn_left'>
              <div className='C2wLanding_Content_ctn_left_content'>
                {renderedLeftDiv}
              </div>

              <motion.div

                className='C2wLeftCircleIcons_motion'
                initial={{ x: 0, scale: 0, opacity: 0 }}
                animate={{ x: 0, scale: 1, opacity: 1 }}
                transition={{ delay: 5.5, duration: 2, ease: "easeInOut" }}
              >
                <img className='C2wLeftCircleIcons' src='/assets/images/landing/leftCircleIcons.svg' alt='leftCircleIcons' />
              </motion.div>
            </div>
          </motion.div>

          <div className='C2wLanding_Content_ctn_center'>
            <motion.div

              className='C2w_left-arrow-svg_motion'
              layout
              initial={{ y: 20, x: 250, opacity: 0 }}
              animate={{ y: 0, x: 0, opacity: 1 }}
              transition={{ delay: 3.5, duration: 2.5, ease: "easeInOut" }}
            >
              <img className='C2w_left-arrow-svg' src='/assets/images/landing/leftArrow.svg' alt='left-arrow-svg' />
            </motion.div>

            <motion.div

              className='C2w_right-arrow-svg_motion'
              layout
              initial={{ y: 200, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 3.5, duration: 2.5, ease: "easeInOut" }}
            >
              <img className='C2w_right-arrow-svg' src='/assets/images/landing/rightArrow.svg' alt='right-arrow-svg' />
            </motion.div>

            <motion.div
              className='C2wLanding_Content_ctn_center_motion'
              layout
              initial={{ y: 230, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 2, duration: 1.5, ease: "easeOut" }}
            >
              <img className='C2wLanding_Content_ctn_center_motion_img' src='/assets/images/landing/ShashiSir.png' alt={'Sir-image'} />
            </motion.div>

          </div>

          <motion.div
            className='C2wLanding_Content_ctn_right_motion'
            layout
            initial={{ y: 300, x: -600, scale: 0, opacity: 0 }}
            animate={{ y: 0, x: 0, scale: 1, opacity: 1 }}
            transition={{ delay: 3.8, duration: 1.8, ease: "easeInOut" }}
          >
            <div className='C2wLanding_Content_ctn_right'>
              <motion.div
                className='C2wRightCircleIcons_motion'
                initial={{ x: 0, scale: 0, opacity: 0 }}
                animate={{ x: 0, scale: 1, opacity: 1 }}
                transition={{ delay: 5.5, duration: 2, ease: "easeInOut" }}
              >
                <img className='C2wRightCircleIcons' src='/assets/images/landing/rightCirclesIcons.svg' alt='leftCircleIcons' />
              </motion.div>
              {renderedEventsCards}
            </div>
          </motion.div>
        </div>

        <motion.div
          className='C2wLanding_Content_Name_holder_motion'
          layout
          initial={{ x: -200, scale: 0, opacity: 0 }}
          animate={{ x: 0, scale: 1, opacity: 1 }}
          transition={{ delay: 5.5, duration: 2, ease: "easeInOut" }}
        >
          <div className='C2wLanding_Content_Name_holder'>
            <div className='C2wLanding_Content_Name_span'>
              <span className='C2wLanding_Content_Name_span1'>{'SHASHI BAGAL'}</span>
              <span className='C2wLanding_Content_Name_span2'>{'Teacher'}</span>
            </div>
            <div className='C2wLanding_Content_ocialMedia_icons'>
              <a href='https://www.linkedin.com/in/shashi-bagal-36b392b7/' target='_blank'><img src='/assets/images/landing/linkedin.svg' alt='linkedin-icon' /></a>
              <a href='https://www.instagram.com/c2wtech_shashi?igsh=MXN1bHFnY3UwOGN2Yw==' target='_blank'><img src='/assets/images/landing/Instagram.svg' alt='insta-icon' /></a>
            </div>
          </div>
        </motion.div>
      </div>
    ) : (
      <div className='C2WLanding_ctn'>
        <motion.div className='C2w_LogoAnimation'
          initial={{ scale: 1, opacity: 1 }}
          animate={{ scale: 0, opacity: 0 }}
          transition={{ delay: 0.5, duration: 2, ease: "easeOut" }}
        >
          <img src='/assets/images/landing/Core2web.png' alt='c2w-img' />
        </motion.div >

        <div className='C2WLanding_heading_ctn'>
          <motion.div
            className='C2wLeftCircleIcons_motion'
            initial={{ x: 0, scale: 0, opacity: 0 }}
            animate={{ x: 0, scale: 1, opacity: 1 }}
            transition={{ delay: 4, duration: 1.5, ease: [0.25, 0.8, 0.25, 1] }}
          >
            <img className='C2wLeftCircleIcons' src='/assets/images/landing/leftCircleIconsMob.svg' alt='leftCircleIcons' />
          </motion.div>

          <motion.div
            className='C2wRightCircleIcons_motion'
            initial={{ x: 0, scale: 0, opacity: 0 }}
            animate={{ x: 0, scale: 1, opacity: 1 }}
            transition={{ delay: 4, duration: 1.5, ease: [0.25, 0.8, 0.25, 1] }}
          >
            <img className='C2wRightCircleIcons' src='/assets/images/landing/rightCircleIconsMob.svg' alt='leftCircleIcons' />
          </motion.div>

          <motion.div className='C2WLanding_heading_motion' initial={{ y: 0, opacity: 0 }} animate={{ y: 110, opacity: 1 }} transition={{ delay: 3, duration: 2, ease: "easeOut" }}>
            <div className='C2WLanding_heading'>
              <div className='C2WLanding_heading_main'>{'IT Training'}</div>
              <div className='C2WLanding_heading_main'>{'Programming Expertise'}</div>
              <div className='C2WLanding_heading_Submain'>{'Hands-On Projects'}</div>
              <div className='C2WLanding_heading_Submain'>{'Entrepreneurship'}</div>
            </div>
          </motion.div>
        </div>

        <div className='C2wLanding_Content_ctn_center'>
          <motion.div
            className='C2wLanding_Content_ctn_center_motion'
            initial={{ y: 200, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 3, duration: 2, ease: "easeOut" }}
          >
            <img className='C2wLanding_Content_ctn_center_motion_img' src='/assets/images/landing/ShashiSirMobile.png' alt={'Sir-image'} />
          </motion.div>

          <motion.div
            className='C2wLanding_Content_Name_holder_motion'
            initial={{ x: -200, scale: 0, opacity: 0 }}
            animate={{ x: 0, scale: 1, opacity: 1 }}
            transition={{ delay: 4, duration: 1.5, ease: 'easeOut' }}
          >
            <div className='C2wLanding_Content_Name_holder'>
              <div className='C2wLanding_Content_Name_span'>
                <span className='C2wLanding_Content_Name_span1'>{'SHASHI BAGAL'}</span>
                <span className='C2wLanding_Content_Name_span2'>{'Teacher'}</span>
              </div>
              <div className='C2wLanding_Content_ocialMedia_icons'>
                <a href="https://www.linkedin.com/in/shashi-bagal-36b392b7/" rel="noreferrer" target="_blank">
                  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.863 2.67314H3.98044C3.27906 2.66525 2.70388 3.2271 2.69531 3.92847V15.873C2.70361 16.5747 3.27872 17.1371 3.98037 17.1297H15.863C16.5662 17.1384 17.1435 16.576 17.1533 15.873V3.92731C17.1433 3.22458 16.5658 2.6628 15.863 2.67199" fill="white" />
                    <path d="M16.2375 16.2095H13.5817V12.0502C13.5817 11.0584 13.564 9.78166 12.2004 9.78166C10.8171 9.78166 10.6055 10.8623 10.6055 11.978V16.2092H7.9497V7.65615H10.4992V8.82502H10.535C10.7901 8.38876 11.1588 8.02987 11.6018 7.78656C12.0448 7.54326 12.5455 7.42465 13.0505 7.44336C15.7424 7.44336 16.2387 9.21396 16.2387 11.5174L16.2375 16.2095ZM4.95295 6.48707C4.10175 6.48721 3.41159 5.79725 3.41145 4.94605C3.41131 4.09485 4.10119 3.40469 4.95239 3.40455C5.8036 3.40434 6.49376 4.09429 6.4939 4.94549C6.49397 5.35426 6.33167 5.74631 6.04269 6.03541C5.75371 6.32451 5.36172 6.48697 4.95295 6.48707ZM6.2809 16.2095H3.62227V7.65615H6.28083L6.2809 16.2095ZM18.8914 4.92116C18.8914 2.72105 17.1079 0.937513 14.9078 0.937511L4.9485 0.937503C2.74839 0.937501 0.964844 2.72104 0.964844 4.92115V14.8803C0.964844 17.0804 2.74838 18.8639 4.94849 18.8639H14.9078C17.1079 18.8639 18.8914 17.0804 18.8914 14.8803V4.92116Z" fill="#0A66C2" />
                  </svg>

                  {/* <img src="/assets/images/landing/linkedin.svg" alt="LinkedIn profile of Shashi Bagal" /> */}
                </a>

                <a href="https://www.instagram.com/c2wtech_shashi?igsh=MXN1bHFnY3UwOGN2Yw==" rel="noreferrer" target="_blank">
                  <img src="/assets/images/landing/Instagram.svg" alt="Instagram profile of Shashi Bagal" />

                </a>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className='C2wLanding_Content_ctn_right_motion'
          initial={{ y: 300, x: -600, scale: 0, opacity: 0 }}
          animate={{ y: 0, x: 0, scale: 1, opacity: 1 }}
          transition={{ delay: 3, duration: 1.5, ease: [0.25, 0.8, 0.25, 1] }}
        >
          <div className='C2wLanding_Content_ctn_right'>
            {renderedEventsCards}
          </div>
        </motion.div>

        <motion.div
          className='C2wLanding_Content_ctn_left_motion'
          initial={{ y: 300, x: 600, scale: 0, opacity: 0 }}
          animate={{ y: 0, x: 0, scale: 1, opacity: 1 }}
          transition={{ delay: 2, duration: 1.5, ease: [0.25, 0.8, 0.25, 1] }}
        >
          <div className='C2wLanding_Content_ctn_left'>
            <div className='C2wLanding_Content_ctn_left_content'>
              {renderedLeftDiv}
            </div>
          </div>
        </motion.div>
      </div>
    )
  )
}

export default C2wLanding
